<template>
  <div class="fs-page">
    <div class="fs-inner">
      <!-- Hero -->
      <div class="hero">
        <div class="hero-txt">
          <div class="hero-eyebrow">PORTAL HR</div>
          <h1 class="hero-title">Fluturaș de salariu</h1>
          <p class="hero-sub">Selectează luna și vizualizează fluturașul tău de salariu.</p>
        </div>
        <div class="hero-actions">
          <v-select
            v-model="lunaSelectata"
            :items="luni"
            item-title="text"
            item-value="val"
            label="Luna"
            variant="solo"
            density="compact"
            hide-details
            bg-color="white"
            style="min-width: 200px"
            :disabled="loadingLuni || luni.length === 0"
          />
          <v-btn class="btn-nou" prepend-icon="mdi-eye-outline" :loading="loadingPdf" :disabled="!lunaSelectata" @click="vizualizeaza">
            Vizualizează
          </v-btn>
          <v-btn class="btn-ghost" prepend-icon="mdi-download" :disabled="!pdfUrl" @click="descarca">
            Descarcă
          </v-btn>
        </div>
      </div>

      <v-alert v-if="eroare" type="error" variant="tonal" class="mb-4" closable @click:close="eroare = ''">
        {{ eroare }}
      </v-alert>

      <div v-if="!loadingLuni && luni.length === 0" class="empty-card">
        <v-icon size="42" color="#ff5722">mdi-cash-remove</v-icon>
        <p>Nu există calcule de salariu disponibile pentru contul tău.</p>
      </div>

      <!-- Viewer PDF -->
      <div v-else class="viewer-card">
        <div class="viewer-head">
          <v-icon size="18" color="#ff5722" class="mr-2">mdi-file-pdf-box</v-icon>
          <span>Fluturaș {{ lunaLabel }}</span>
        </div>
        <div class="viewer-body">
          <v-progress-linear v-if="loadingPdf" indeterminate color="#ff5722" />
          <iframe v-if="pdfUrl" :src="pdfUrl" class="pdf-frame" title="Fluturaș de salariu"></iframe>
          <div v-else-if="!loadingPdf" class="viewer-placeholder">
            <v-icon size="42" color="#cbd5e1">mdi-file-eye-outline</v-icon>
            <p>Alege o lună și apasă „Vizualizează".</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PORTAL_Fluturas from '@/services/Portal/PORTAL_Fluturas'

export default defineComponent({
  name: 'FluturasSalariu',
  data() {
    return {
      loadingLuni: false,
      loadingPdf: false,
      eroare: '',
      employeeId: '' as string,
      luni: [] as { val: string; text: string }[],
      lunaSelectata: '' as string,
      pdfUrl: '' as string,
    }
  },
  computed: {
    lunaLabel(): string {
      return this.luni.find(l => l.val === this.lunaSelectata)?.text ?? ''
    }
  },
  mounted() { this.incarcaLuni() },
  beforeUnmount() { this.revokeUrl() },
  methods: {
    async incarcaLuni() {
      this.loadingLuni = true
      this.eroare = ''
      try {
        const { data } = await PORTAL_Fluturas.getLuniDisponibile()
        this.employeeId = data.EmployeeID
        const fmt = new Intl.DateTimeFormat('ro-RO', { month: 'long', year: 'numeric' })
        this.luni = (data.Luni ?? []).map((d: string) => ({
          val: d,
          text: this.capitalize(fmt.format(new Date(d)))
        }))
        if (this.luni.length > 0) this.lunaSelectata = this.luni[0].val
      } catch (e: any) {
        this.eroare = e?.response?.data?.message || 'Nu s-au putut încărca lunile disponibile.'
      } finally {
        this.loadingLuni = false
      }
    },
    async vizualizeaza() {
      if (!this.lunaSelectata) return
      this.loadingPdf = true
      this.eroare = ''
      this.revokeUrl()
      try {
        const resp = await PORTAL_Fluturas.getFluturasPdf(this.employeeId, this.lunaSelectata)
        const blob = new Blob([resp.data], { type: 'application/pdf' })
        this.pdfUrl = URL.createObjectURL(blob)
      } catch (e: any) {
        this.eroare = 'Nu s-a putut genera fluturașul pentru luna selectată.'
      } finally {
        this.loadingPdf = false
      }
    },
    descarca() {
      if (!this.pdfUrl) return
      const a = document.createElement('a')
      a.href = this.pdfUrl
      a.download = `fluturas-salariu-${this.lunaSelectata?.slice(0, 7)}.pdf`
      a.click()
    },
    revokeUrl() {
      if (this.pdfUrl) { URL.revokeObjectURL(this.pdfUrl); this.pdfUrl = '' }
    },
    capitalize(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1) }
  }
})
</script>

<style scoped>
.fs-page { padding: 16px; width: 100%; }
.fs-inner { width: 100%; }

.hero {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%);
  border-radius: 18px; padding: 24px 28px; color: #fff;
  box-shadow: 0 10px 30px rgba(255, 87, 34, .28); margin-bottom: 18px;
}
.hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; opacity: .85; }
.hero-title { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 4px 0 2px; }
.hero-sub { font-size: 13px; opacity: .92; margin: 0; }
.hero-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.btn-nou { background: #fff !important; color: #ff5722 !important; font-weight: 700; border-radius: 22px; text-transform: none; letter-spacing: 0; }
.btn-ghost { background: rgba(255,255,255,.2) !important; color: #fff !important; font-weight: 700; border-radius: 22px; text-transform: none; letter-spacing: 0; }

.empty-card {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  background: #fff; border: 1px solid #eef1f6; border-radius: 18px; padding: 48px 20px;
  color: #64748b; box-shadow: 0 1px 3px rgba(15, 23, 42, .05);
}

.viewer-card { background: #fff; border: 1px solid #eef1f6; border-radius: 18px; box-shadow: 0 1px 3px rgba(15, 23, 42, .05); overflow: hidden; }
.viewer-head { display: flex; align-items: center; padding: 16px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; font-weight: 700; color: #1e293b; }
.viewer-body { position: relative; min-height: 620px; }
.pdf-frame { width: 100%; height: 78vh; border: 0; display: block; }
.viewer-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
  min-height: 620px; color: #94a3b8;
}
</style>
